/**
 * Project Dashboard - Single project view with tabs
 */

import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { useState } from "react"

import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"
import { UpdatesList } from "./components/UpdatesList"
import { ChannelsList } from "./components/ChannelsList"
import { BuildsList } from "./components/BuildsList"
import { DeploymentLogsTable } from "./components/DeploymentLogsTable"
import { DebugTab } from "./DebugTab"

export function ProjectDashboard() {
  const navigate = useNavigate()
  const { projectSlug } = useParams({ strict: false }) as { projectSlug: string }
  const search = useSearch({ strict: false }) as { tab?: string; channel?: string }

  const activeTab = search.tab || "overview"
  const selectedChannel = search.channel || "production"

  // Fetch project
  const { data: project, isLoading: isLoadingProject } = trpc.easUpdates.getProject.useQuery({
    slug: projectSlug,
  })

  // Fetch updates for selected channel
  const {
    data: updatesData,
    isLoading: isLoadingUpdates,
    refetch: refetchUpdates,
  } = trpc.easUpdates.listUpdates.useQuery(
    {
      projectSlug,
      channel: selectedChannel,
      limit: 50,
    },
    { enabled: !!project && activeTab === "updates" },
  )

  // Fetch channels
  const { data: channelsData, isLoading: isLoadingChannels } = trpc.easUpdates.listChannels.useQuery(
    { projectSlug },
    { enabled: !!project && (activeTab === "channels" || activeTab === "overview") },
  )

  // Fetch builds
  const { data: buildsData, isLoading: isLoadingBuilds } = trpc.easUpdates.listBuilds.useQuery(
    { projectSlug, limit: 20 },
    { enabled: !!project && activeTab === "builds" },
  )

  // Fetch deployment logs
  const { data: logsData, isLoading: isLoadingLogs } = trpc.easUpdates.listDeploymentLogs.useQuery(
    { projectSlug, limit: 50 },
    { enabled: !!project && activeTab === "history" },
  )

  const handleTabChange = (tab: string) => {
    navigate({
      to: `/eas-dashboard/${projectSlug}`,
      // @ts-ignore
      search: (s) => ({ ...s, tab }),
    })
  }

  const handleChannelChange = (channel: string) => {
    navigate({
      to: `/eas-dashboard/${projectSlug}`,
      // @ts-ignore
      search: (s) => ({ ...s, channel }),
    })
  }

  if (isLoadingProject) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading project...</div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Project not found</p>
          <Button onClick={() => navigate({ to: "/eas-dashboard" })}>← Back to Dashboard</Button>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "updates", label: "Updates" },
    { id: "channels", label: "Channels" },
    { id: "builds", label: "Builds" },
    { id: "history", label: "History" },
    { id: "debug", label: "Debug" },
  ]

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <Button onClick={() => navigate({ to: "/eas-dashboard" })} className="mb-4 bg-secondary">
          ← Back to All Projects
        </Button>

        <div className="flex items-center gap-4 mb-4">
          {project.icon && <span className="text-4xl">{project.icon}</span>}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-foreground">{project.name}</h1>
            <p className="text-muted-foreground">@{project.slug}</p>
          </div>
          {/* Company Badge - TODO: Add company relation to getProject query */}
          {/* {project.company && (
            <button
              onClick={() => navigate({ to: `/companies/${project.company.slug}` })}
              className="px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors flex items-center gap-2"
              title={`View ${project.company.name} details`}
            >
              {project.company.color && (
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: project.company.color }}
                />
              )}
              <span className="text-sm font-medium">{project.company.name}</span>
              <span className="text-xs text-muted-foreground font-mono">
                ({project.company.infisicalPath})
              </span>
            </button>
          )} */}
        </div>

        {project.description && <p className="text-muted-foreground">{project.description}</p>}

        {/* Platforms & Channels */}
        <div className="flex flex-wrap gap-2 mt-4">
          {project.platforms.map((platform) => (
            <span key={platform} className="px-3 py-1 bg-secondary text-secondary-foreground text-sm rounded">
              {platform === "ios" ? "📱 iOS" : "🤖 Android"}
            </span>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`pb-3 px-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "overview" && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Project Overview</h2>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-secondary rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Platforms</p>
                <p className="text-xl font-bold text-foreground">{project.platforms.length}</p>
              </div>
              <div className="bg-secondary rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Channels</p>
                <p className="text-xl font-bold text-foreground">{project.channels.length}</p>
              </div>
              <div className="bg-secondary rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Status</p>
                <p className="text-xl font-bold text-green-600">{project.isActive ? "Active" : "Inactive"}</p>
              </div>
            </div>

            {/* Recent Channels */}
            {channelsData && (
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Channels</h3>
                <ChannelsList channels={channelsData.channels} isLoading={isLoadingChannels} />
              </div>
            )}
          </div>
        )}

        {activeTab === "updates" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-foreground">Updates</h2>

              {/* Channel Selector */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Channel:</span>
                <select
                  value={selectedChannel}
                  onChange={(e) => handleChannelChange(e.target.value)}
                  className="border border-border rounded-md px-3 py-2 bg-background text-foreground"
                >
                  {project.channels.map((channel) => (
                    <option key={channel} value={channel}>
                      {channel}
                    </option>
                  ))}
                </select>
                <Button onClick={() => refetchUpdates()} loading={isLoadingUpdates}>
                  Refresh
                </Button>
              </div>
            </div>

            <UpdatesList
              updateGroups={updatesData?.updateGroups || []}
              isLoading={isLoadingUpdates}
              projectSlug={projectSlug}
            />
          </div>
        )}

        {activeTab === "channels" && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Channels</h2>
            <ChannelsList channels={channelsData?.channels || []} isLoading={isLoadingChannels} />
          </div>
        )}

        {activeTab === "builds" && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Builds</h2>
            <BuildsList builds={buildsData?.builds || []} isLoading={isLoadingBuilds} />
          </div>
        )}

        {activeTab === "history" && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Deployment History</h2>
            <DeploymentLogsTable logs={logsData || []} isLoading={isLoadingLogs} />
          </div>
        )}

        {activeTab === "debug" && (
          <DebugTab
            project={project}
            updatesData={updatesData}
            channelsData={channelsData}
            buildsData={buildsData}
            logsData={logsData}
          />
        )}
      </div>
    </div>
  )
}
